/**
 * The thread view's state machine (design section 6.2/6.4).
 *
 * Everything with a rule in it is here, so `tests/unit/admin/support-inbox-page.test.ts`
 * can EXECUTE it through `react-test-renderer`: the read, the composer, the
 * reply's success and refusal paths, the status and assignee writes, the
 * erase, and the two presigned downloads.
 *
 * ── THE REPLY ──────────────────────────────────────────────────────────────
 * `submitReply` POSTs the composer text and, on success, clears the composer
 * and APPENDS the outbound row the API confirmed (`outboundRowFromReply`) —
 * the thread is not re-read, because the row the API just wrote is exactly
 * what a re-read would return and a second round trip would only make the
 * reply look slower than it was. The thread's status follows the API's
 * `thread_status` (`pending`). A refusal (409 machine thread, 429 rate cap,
 * 409 non-prod gate, 500 send failed) keeps the text in the composer, sets
 * `error` to the server's message VERBATIM and hands it to `onError`, which
 * the page binds to a destructive toast. Nothing is appended on a refusal.
 *
 * ── THE COLLISION GUARD (round 2, B5) ──────────────────────────────────────
 * The POST carries `expected_last_message_at`: the thread's `last_message_at`
 * THIS page rendered. When the thread changed since (a new inbound message,
 * a colleague's reply), the route answers 409 `support_thread_moved` and
 * sends nothing; the hook sets `moved`, keeps the text, and the page shows
 * the server's sentence with a Refresh action. `refresh()` clears `moved`
 * and re-reads the thread, which re-enables the composer with the NEW
 * `last_message_at` in hand.
 *
 * ── MACHINE → HUMAN (round 2, B11) ─────────────────────────────────────────
 * `reopenAsHuman()` PATCHes `{ is_machine: false }`; the route turns the
 * thread human and open, or refuses (409 `support_thread_machine_sender`)
 * when the thread is a DSN by shape. `canReopenMachine` is the write key AND
 * a machine thread — the page shows the control on nothing else.
 *
 * ── canReply ───────────────────────────────────────────────────────────────
 * False on a machine thread, false when there is no human inbound message to
 * answer, false without `support_inbox.write`, and false while `moved` is
 * set. The composer is not rendered when it is false — a disabled composer
 * would still show a "Reply to" line for an address that must never receive
 * a reply. (An own copy — our reply received back through the inbox — is
 * never the target: the server skips it, and the page renders it as one
 * line.)
 *
 * ── THE ASSIGNEE SELECT ────────────────────────────────────────────────────
 * `GET /v1/admin/admins` is gated by `admin.list`, which a `support` admin
 * does not hold. The select therefore always offers "Unassigned", the current
 * admin and the current assignee, and adds the full list only when the admin
 * may read it. A failed admins read never blocks the page.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import {
  ADMIN_LIST_PERMISSION,
  DELETE_PERMISSION,
  THREAD_MOVED_CODE,
  WRITE_PERMISSION,
  attachmentViews,
  deleteThreadRequest,
  errorCode,
  errorMessage,
  errorStatus,
  fetchAdmins,
  fetchAttachmentDownload,
  fetchRawDownload,
  fetchThread,
  openInNewTab,
  outboundRowFromReply,
  patchThread,
  postReply,
  type AdminListRow,
  type AttachmentView,
  type SupportMemberCard,
  type SupportMessageRow,
  type SupportThreadErasedData,
  type SupportThreadRow,
  type ThreadStatus,
} from './data';

export interface AssigneeOption {
  id: string;
  label: string;
}

export interface SupportThreadController {
  thread: SupportThreadRow | null;
  messages: SupportMessageRow[];
  attachments: AttachmentView[];
  member: SupportMemberCard | null;
  composerText: string;
  setComposerText: (text: string) => void;
  sending: boolean;
  /** The last refusal's server message, verbatim. Cleared by the next attempt. */
  error: string | null;
  /** The address a reply goes to — the last human inbound message's From. */
  replyToAddress: string | null;
  /** False on a machine thread, with no reply target, without the write key, or while `moved`. */
  canReply: boolean;
  /**
   * The last reply was refused with 409 `support_thread_moved`: the thread
   * changed after the composer rendered. The text is kept; `refresh()` clears it.
   */
  moved: boolean;
  submitReply: (e?: { preventDefault: () => void }) => Promise<void>;
  setStatus: (status: ThreadStatus) => Promise<void>;
  assign: (adminId: string | null) => Promise<void>;
  /** Machine → human: PATCH `{ is_machine: false }` (round 2, B11). */
  reopenAsHuman: () => Promise<void>;
  /** The write key on a machine thread — the only place the reopen control shows. */
  canReopenMachine: boolean;
  /** True while a status or assignee write is in flight. */
  updating: boolean;
  loading: boolean;
  accessDenied: boolean;
  notFound: boolean;
  canWrite: boolean;
  canDelete: boolean;
  /** The two-step erase: `armDelete` shows the inline confirm, `deleteThread` performs it. */
  deleteArmed: boolean;
  armDelete: () => void;
  disarmDelete: () => void;
  deleteThread: () => Promise<void>;
  deleting: boolean;
  /** Set once the DELETE succeeded; the page then leaves for the list. */
  deleted: SupportThreadErasedData | null;
  /** Presigned `.eml` of a message, opened in a new tab. */
  openRaw: (messageId: string) => Promise<void>;
  /** Presigned attachment download, opened in a new tab. Refused unless `downloadAllowed`. */
  downloadAttachment: (attachmentId: string) => Promise<void>;
  assigneeOptions: AssigneeOption[];
  refresh: () => void;
}

export interface SupportThreadPageOptions {
  /** Bound by the page to a destructive toast. Receives the server's message verbatim. */
  onError?: (message: string) => void;
  /** Test seam: where a presigned URL goes. Defaults to a new tab with `noopener`. */
  openUrl?: (url: string) => void;
}

export function useSupportThreadPage(
  threadId: string | undefined,
  options: SupportThreadPageOptions = {},
): SupportThreadController {
  const apiUrl = useApiUrl();
  const { hasPermission, admin } = useAdminPermissions();
  const canWrite = hasPermission(WRITE_PERMISSION);
  const canDelete = hasPermission(DELETE_PERMISSION);
  const canListAdmins = hasPermission(ADMIN_LIST_PERMISSION);
  // Held in a ref: the page passes inline callbacks, and a callback whose
  // identity changes every render must not re-run the thread read.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [thread, setThread] = useState<SupportThreadRow | null>(null);
  const [messages, setMessages] = useState<SupportMessageRow[]>([]);
  const [attachments, setAttachments] = useState<AttachmentView[]>([]);
  const [member, setMember] = useState<SupportMemberCard | null>(null);
  const [replyToAddress, setReplyToAddress] = useState<string | null>(null);
  const [serverCanReply, setServerCanReply] = useState(false);
  const [composerText, setComposerText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState<SupportThreadErasedData | null>(null);
  const [admins, setAdmins] = useState<AdminListRow[]>([]);
  const [moved, setMoved] = useState(false);
  const [tick, setTick] = useState(0);

  const fail = useCallback((e: unknown) => {
    const message = errorMessage(e);
    setError(message);
    // The collision guard's refusal is a STATE, not only a toast: the composer
    // stays closed until the operator refreshes and reads what arrived.
    if (errorCode(e) === THREAD_MOVED_CODE) setMoved(true);
    const { onError } = optionsRef.current;
    if (onError) onError(message);
  }, []);

  const load = useCallback(async () => {
    if (!threadId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchThread(apiUrl, threadId);
      setThread(data.thread);
      setMessages(data.messages);
      setAttachments(attachmentViews(data.attachments, data.messages));
      setMember(data.member);
      setReplyToAddress(data.reply_to_address);
      setServerCanReply(data.can_reply);
      setAccessDenied(false);
      setNotFound(false);
    } catch (e) {
      const status = errorStatus(e);
      if (status === 403) setAccessDenied(true);
      else if (status === 404) setNotFound(true);
      else fail(e);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, threadId, fail]);

  useEffect(() => {
    void load();
  }, [load, tick]);

  useEffect(() => {
    if (!canWrite || !canListAdmins) return;
    let cancelled = false;
    fetchAdmins(apiUrl)
      .then((rows) => {
        if (!cancelled) setAdmins(rows.filter((r) => r.is_active));
      })
      .catch(() => {
        // The select still offers "me" and "unassigned"; the list is a convenience.
        if (!cancelled) setAdmins([]);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, canWrite, canListAdmins]);

  const canReply =
    canWrite && thread !== null && !thread.is_machine && serverCanReply && replyToAddress !== null && !moved;
  const canReopenMachine = canWrite && thread !== null && thread.is_machine;

  const submitReply = useCallback(
    async (e?: { preventDefault: () => void }) => {
      if (e) e.preventDefault();
      if (!thread || !canReply || sending) return;
      const text = composerText;
      if (text.trim().length === 0) return;
      setSending(true);
      setError(null);
      try {
        // The thread's last_message_at THIS page rendered rides along: the
        // route refuses when the thread moved since (the collision guard).
        const reply = await postReply(apiUrl, thread.id, text, thread.last_message_at);
        setComposerText('');
        setMessages((prev) => [...prev, outboundRowFromReply(reply, thread, text, admin?.id ?? null)]);
        // The stamps come from the RESPONSE, never from this browser's clock:
        // the next reply sends `last_message_at` back as the guard key, and the
        // route compares it with the stored value to the millisecond.
        setThread((prev) =>
          prev
            ? {
                ...prev,
                status: reply.thread_status,
                message_count: prev.message_count + 1,
                last_outbound_at: reply.last_message_at,
                last_message_at: reply.last_message_at,
              }
            : prev,
        );
      } catch (err) {
        fail(err);
      } finally {
        setSending(false);
      }
    },
    [apiUrl, thread, canReply, sending, composerText, admin, fail],
  );

  const setStatus = useCallback(
    async (status: ThreadStatus) => {
      if (!thread || !canWrite || updating) return;
      setUpdating(true);
      setError(null);
      try {
        setThread(await patchThread(apiUrl, thread.id, { status }));
      } catch (err) {
        fail(err);
      } finally {
        setUpdating(false);
      }
    },
    [apiUrl, thread, canWrite, updating, fail],
  );

  const assign = useCallback(
    async (adminId: string | null) => {
      if (!thread || !canWrite || updating) return;
      setUpdating(true);
      setError(null);
      try {
        setThread(await patchThread(apiUrl, thread.id, { assigned_admin_id: adminId }));
      } catch (err) {
        fail(err);
      } finally {
        setUpdating(false);
      }
    },
    [apiUrl, thread, canWrite, updating, fail],
  );

  const armDelete = useCallback(() => {
    if (canDelete) setDeleteArmed(true);
  }, [canDelete]);
  const disarmDelete = useCallback(() => setDeleteArmed(false), []);

  const deleteThread = useCallback(async () => {
    // The inline confirm is the gate: an un-armed call does nothing, so a
    // stray click on the first control can never erase.
    if (!thread || !canDelete || !deleteArmed || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      setDeleted(await deleteThreadRequest(apiUrl, thread.id));
    } catch (err) {
      fail(err);
    } finally {
      setDeleting(false);
      setDeleteArmed(false);
    }
  }, [apiUrl, thread, canDelete, deleteArmed, deleting, fail]);

  const open = useCallback((url: string) => {
    const { openUrl } = optionsRef.current;
    (openUrl ?? openInNewTab)(url);
  }, []);

  const openRaw = useCallback(
    async (messageId: string) => {
      try {
        const { url } = await fetchRawDownload(apiUrl, messageId);
        open(url);
      } catch (err) {
        fail(err);
      }
    },
    [apiUrl, open, fail],
  );

  const downloadAttachment = useCallback(
    async (attachmentId: string) => {
      const att = attachments.find((a) => a.id === attachmentId);
      if (!att || !att.downloadAllowed) return;
      try {
        const { url } = await fetchAttachmentDownload(apiUrl, attachmentId);
        open(url);
      } catch (err) {
        fail(err);
      }
    },
    [apiUrl, attachments, open, fail],
  );

  const assigneeOptions = useMemo<AssigneeOption[]>(() => {
    const byId = new Map<string, AssigneeOption>();
    if (admin) byId.set(admin.id, { id: admin.id, label: `${admin.display_name || admin.email} (me)` });
    if (thread?.assigned_admin_id && !byId.has(thread.assigned_admin_id)) {
      byId.set(thread.assigned_admin_id, {
        id: thread.assigned_admin_id,
        label: thread.assigned_admin_display_name || thread.assigned_admin_id,
      });
    }
    for (const a of admins) {
      if (!byId.has(a.id)) byId.set(a.id, { id: a.id, label: a.display_name || a.email });
    }
    return [...byId.values()];
  }, [admin, admins, thread?.assigned_admin_id, thread?.assigned_admin_display_name]);

  const reopenAsHuman = useCallback(async () => {
    if (!thread || !canReopenMachine || updating) return;
    setUpdating(true);
    setError(null);
    try {
      setThread(await patchThread(apiUrl, thread.id, { is_machine: false }));
      // The route also clears `is_machine` on the thread's inbound ROWS and
      // recomputes the reply target (#3171), and neither comes back in the
      // PATCH response. Re-read the thread so the composer appears at once;
      // without this the page kept its stale rows and read "No human inbound
      // message to answer" until a manual Refresh (PREDEV click, 2026-09-28).
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setUpdating(false);
    }
  }, [apiUrl, thread, canReopenMachine, updating, fail, load]);

  // A refresh re-reads the thread AND clears the collision state: the new
  // `last_message_at` arrives with the read, so the next reply carries it.
  const refresh = useCallback(() => {
    setMoved(false);
    setError(null);
    setTick((t) => t + 1);
  }, []);

  return {
    thread,
    messages,
    attachments,
    member,
    composerText,
    setComposerText,
    sending,
    error,
    replyToAddress,
    canReply,
    moved,
    submitReply,
    setStatus,
    assign,
    reopenAsHuman,
    canReopenMachine,
    updating,
    loading,
    accessDenied,
    notFound,
    canWrite,
    canDelete,
    deleteArmed,
    armDelete,
    disarmDelete,
    deleteThread,
    deleting,
    deleted,
    openRaw,
    downloadAttachment,
    assigneeOptions,
    refresh,
  };
}
